import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Typography,
  List,
  Avatar,
  Input,
  Button,
  Divider,
  Empty,
  Spin,
  message,
  Tooltip,
} from "antd";
import {
  SendOutlined,
  CommentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import "../Dialog/ObservacionesDialog.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

function ObservacionesAutorizacionDialog({ open, onClose, autorizacionId, onObservacionAdded }) {
  const { user } = useUser();
  const [observaciones, setObservaciones] = useState([]);
  const [nuevaObservacion, setNuevaObservacion] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const listEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (autorizacionId && open) {
      setLoading(true);
      api
        .get(`/observaciones-autorizacion/${autorizacionId}`)
        .then((response) => {
          setObservaciones(response.data);
          setTimeout(scrollToBottom, 100);
        })
        .catch((error) => {
          console.error("Error al obtener las observaciones:", error);
          message.error("Error al cargar las observaciones");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [autorizacionId, open]);

  const handleAgregarObservacion = async () => {
    if (!nuevaObservacion.trim() || !user) return;

    setSending(true);
    try {
      const response = await api.post("/observaciones-autorizacion", {
        autorizacion_id: autorizacionId,
        usuario_id: user.id,
        texto_observacion: nuevaObservacion,
      });

      const observacion = response.data.observacion || response.data;
      const nuevaObservacionConUsuario = {
        ...observacion,
        Usuario: { nombre: user.nombre },
      };

      setObservaciones([...observaciones, nuevaObservacionConUsuario]);
      setNuevaObservacion("");
      message.success("Observacion agregada correctamente");
      setTimeout(scrollToBottom, 100);

      // Notificar al componente padre si hay callback
      if (onObservacionAdded) {
        onObservacionAdded(response.data.observaciones_count);
      }
    } catch (error) {
      console.error("Error al agregar la observacion:", error);
      message.error("Error al agregar la observacion");
    } finally {
      setSending(false);
    }
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    const ahora = new Date();
    const diferencia = ahora - date;
    const minutos = Math.floor(diferencia / 60000);
    const horas = Math.floor(diferencia / 3600000);
    const dias = Math.floor(diferencia / 86400000);

    if (minutos < 1) return "Hace un momento";
    if (minutos < 60) return `Hace ${minutos} min`;
    if (horas < 24) return `Hace ${horas}h`;
    if (dias < 7) return `Hace ${dias}d`;
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getAvatarColor = (nombre) => {
    const colors = [
      "#1890ff", "#52c41a", "#faad14", "#f5222d",
      "#722ed1", "#13c2c2", "#eb2f96", "#fa8c16",
    ];
    let hash = 0;
    for (let i = 0; i < nombre.length; i++) {
      hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="observaciones-modal-header">
          <div className="header-icon">
            <CommentOutlined />
          </div>
          <div className="header-content">
            <Title level={4} className="header-title">
              Observaciones de Autorizacion
            </Title>
            <Text type="secondary" className="header-subtitle">
              {observaciones.length} observaciones - Autorizacion #{autorizacionId}
            </Text>
          </div>
        </div>
      }
      width={800}
      centered
      destroyOnHidden
      className="observaciones-modal"
      styles={{
        body: { padding: 0 },
        header: { padding: "24px 24px 0 24px", borderBottom: "none" },
      }}
    >
      <div className="observaciones-container">
        {/* Lista de observaciones */}
        <div
          className="observaciones-list-container"
          ref={scrollContainerRef}
        >
          {loading ? (
            <div className="loading-container">
              <Spin size="large" />
              <Text type="secondary" style={{ marginTop: 16 }}>
                Cargando observaciones...
              </Text>
            </div>
          ) : observaciones.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text type="secondary">No hay observaciones aun</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Se el primero en agregar una observacion
                  </Text>
                </div>
              }
            />
          ) : (
            <List
              dataSource={observaciones}
              renderItem={(observacion, index) => {
                const isOwn =
                  observacion.Usuario &&
                  observacion.Usuario.nombre === user.nombre;
                const isFirst = index === 0;
                const isLast = index === observaciones.length - 1;

                return (
                  <div
                    key={observacion.id}
                    className={`observacion-item ${isOwn ? "own" : "other"} ${
                      isFirst ? "first" : ""
                    } ${isLast ? "last" : ""}`}
                  >
                    <div className="observacion-content">
                      <div className="observacion-avatar">
                        <Avatar
                          style={{
                            backgroundColor: getAvatarColor(
                              observacion.Usuario?.nombre || "Usuario"
                            ),
                            border: "2px solid #fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          {observacion.Usuario?.nombre?.charAt(0).toUpperCase() ||
                            "U"}
                        </Avatar>
                      </div>

                      <div className="observacion-bubble">
                        <div className="observacion-header">
                          <Text strong className="observacion-author">
                            {observacion.Usuario?.nombre || "Usuario desconocido"}
                          </Text>
                          <Tooltip
                            title={new Date(
                              observacion.created_at
                            ).toLocaleString()}
                          >
                            <Text type="secondary" className="observacion-time">
                              <ClockCircleOutlined style={{ marginRight: 4 }} />
                              {formatearFecha(observacion.created_at)}
                            </Text>
                          </Tooltip>
                        </div>

                        <div className="observacion-message">
                          <Text>{observacion.texto_observacion}</Text>
                        </div>

                        <div className="observacion-status">
                          <CheckCircleOutlined style={{ color: "#52c41a" }} />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Enviado
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}
          <div ref={listEndRef} />
        </div>

        {/* Area de entrada */}
        <div className="observaciones-input-container">
          <Divider style={{ margin: "16px 0" }} />

          <div className="input-section">
            <div className="textarea-container">
              <TextArea
                rows={3}
                placeholder="Escribe tu observacion..."
                value={nuevaObservacion}
                onChange={(e) => setNuevaObservacion(e.target.value)}
                className="observacion-textarea"
                maxLength={1000}
                showCount
                disabled={sending}
              />
            </div>

            <div className="actions-container">
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleAgregarObservacion}
                disabled={!nuevaObservacion.trim() || sending}
                loading={sending}
                block
                size="large"
                className="send-button"
              >
                {sending ? "Enviando..." : "Enviar Observacion"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

ObservacionesAutorizacionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  autorizacionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onObservacionAdded: PropTypes.func,
};

export default ObservacionesAutorizacionDialog;
